import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { checkDuplicateCustomers } from '../api';
import './duplicatecustomerprompt.css';

const REASON_LABELS = { name: 'Same name', phone: 'Same phone', email: 'Same email' };

const pluralize = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Shared "possible duplicate customer" check for every screen that can create a
 * customer (New Customer, New Work Order, New Job, New Quote, New Appointment).
 *
 *   const { confirmNewCustomer, duplicatePrompt } = useDuplicateCustomerCheck();
 *   const choice = await confirmNewCustomer({ name, phone, email });
 *   // { action: 'create' }            -> no matches, or staff chose "Create new anyway"
 *   // { action: 'use', customer }     -> staff picked an existing customer
 *   // null                            -> staff went back to edit the form
 *   ...
 *   {duplicatePrompt}   // render once, anywhere in the screen
 *
 * If the check itself fails, it falls through to 'create' so a network hiccup never
 * blocks creating a work order.
 */
const useDuplicateCustomerCheck = () => {
  const [pending, setPending] = useState(null); // { matches, resolve }

  const confirmNewCustomer = useCallback(async ({ name, phone, email }) => {
    let matches = [];
    try {
      matches = await checkDuplicateCustomers({ name, phone, email });
    } catch (error) {
      console.error('Duplicate customer check failed:', error);
    }
    if (matches.length === 0) return { action: 'create' };
    return new Promise((resolve) => setPending({ matches, resolve }));
  }, []);

  const finish = (choice) => {
    pending.resolve(choice);
    setPending(null);
  };

  // Portaled to <body> so it sits above whichever modal opened it, whatever that
  // modal's own stacking (the appointment modal uses z-index 999999).
  const duplicatePrompt = pending ? createPortal(
    <div className="dupcustomer-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="dupcustomer-modal" role="dialog" aria-labelledby="dupcustomer-title">
        <h3 id="dupcustomer-title">Possible existing customer</h3>
        <p className="dupcustomer-intro">
          {pending.matches.length === 1 ? 'This customer' : 'These customers'} may already be on file.
          Use one of them, or create a new customer anyway.
        </p>
        <ul className="dupcustomer-list">
          {pending.matches.map((match) => (
            <li key={match.id} className="dupcustomer-item">
              <div className="dupcustomer-details">
                <div className="dupcustomer-name">{match.name}</div>
                <div className="dupcustomer-contact">
                  {[match.phone, match.email].filter(Boolean).join(' · ') || 'No phone or email on file'}
                </div>
                <div className="dupcustomer-meta">
                  {match.reasons.map((reason) => (
                    <span key={reason} className="dupcustomer-reason">{REASON_LABELS[reason]}</span>
                  ))}
                  <span className="dupcustomer-history">
                    {pluralize(match.work_order_count, 'work order')}, {pluralize(match.quote_count, 'quote')}, {pluralize(match.job_count, 'job')}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="dupcustomer-use"
                onClick={() => finish({ action: 'use', customer: match })}
              >
                Use this customer
              </button>
            </li>
          ))}
        </ul>
        <div className="dupcustomer-actions">
          <button type="button" className="dupcustomer-back" onClick={() => finish(null)}>
            Go back
          </button>
          <button type="button" className="dupcustomer-create" onClick={() => finish({ action: 'create' })}>
            Create new anyway
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return { confirmNewCustomer, duplicatePrompt };
};

export default useDuplicateCustomerCheck;
